import { Component } from '@theme/component';
/**
 * A custom element that displays a sensor registration.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class SensorRegistrationComponent extends Component {
  requiredRefs = ['form'];

  connectedCallback() {
    super.connectedCallback();

    const form = this.querySelector('form');
    if (!form) return;

    this.form = form;

    this.#initForm();
    this.form.addEventListener('submit', this.#handleFormSubmit.bind(this));
  }

  #initForm() {
    this.#initRequiredFields();

    const haveAnotherSensor = this.form?.querySelector('[name="have_another_sensor"]');
    if (!haveAnotherSensor) return;

    haveAnotherSensor.addEventListener('change', this.#initRequiredFields.bind(this));
  }

  #initRequiredFields() {
    const requiredFields = this.form?.querySelectorAll('[data-required]');
    if (!requiredFields?.length) return;

    const haveAnotherSensor = this.form?.querySelector('[name="have_another_sensor"]');
    if (!haveAnotherSensor) return;

    if (haveAnotherSensor instanceof HTMLInputElement && haveAnotherSensor.checked) {
      requiredFields.forEach(field => {
        field.setAttribute('required', 'required');
      });
    } else {
      requiredFields.forEach(field => {
        field.removeAttribute('required');
      });
    }
  }

  #setFormStatus(active = false, status = 'success', message = '') {
    const formStatus = this.form?.querySelector('.sensor-registration__form-status');
    if (!formStatus) return;

    const successEl = formStatus.querySelector('[data-form-status="success"]');
    const errorEl = formStatus.querySelector('[data-form-status="error"]');
    if (!successEl || !errorEl) return;

    if (!(successEl instanceof HTMLElement) || !(errorEl instanceof HTMLElement)) return;

    formStatus.classList.toggle('sensor-registration__form-status--active', active);

    if (status === 'success') {
      successEl.style.display = active ? 'block' : 'none';
      errorEl.style.display = 'none';
      if (!message) return;
      successEl.textContent = message;
    } else {
      successEl.style.display = 'none';
      errorEl.style.display = active ? 'block' : 'none';
      if (!message) return;
      errorEl.textContent = message;
    }
  }

  #setLoading(loading = false) {
    const form = this.form;
    if (!form) return;

    form.classList.toggle('sensor-registration__form--loading', loading);

    const submitButton = form.querySelector('button[type="submit"]');
    if (!(submitButton instanceof HTMLButtonElement)) return;

    submitButton.disabled = loading;
    submitButton.textContent = loading ? 'Please wait...' : 'Submit';
  }

  /**
   * Handles the submit event for the form.
   *
   * @param {Event} event - The submit event.
   */
  #handleFormSubmit = async (event) => {
    event.preventDefault();

    this.#setFormStatus(false);

    const formData = new FormData(this.form);
    const data = Object.fromEntries(formData);

    if (!data.email || !data.company_id) {
      this.#setFormStatus(true, 'error', 'Something went wrong. Please try again later.');
      return;
    }

    if (!data['Sensor Serial # 1'] || (data.have_another_sensor && !data['Sensor Serial # 2'])) {
      this.#setFormStatus(true, 'error', 'Please fill in all required fields.');
      return;
    }

    this.#setLoading(true);

    try {
        await fetch(`https://a.klaviyo.com/client/profiles/?company_id=${data.company_id}`, {
            method: 'POST',
            headers: {
                revision: '2025-10-15',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                data: {
                    type: 'profile',
                    attributes: {
                        email: data.email,
                        properties: {
                            'Sensor Serial # 1': data['Sensor Serial # 1'],
                            ...(data['Order Date # 1'] ? { 'Order Date # 1': data['Order Date # 1'] } : {}),
                            ...(data['Order Number # 1'] ? { 'Order Number # 1': data['Order Number # 1'] } : {}),
                            ...(data.have_another_sensor ? { 'Sensor Serial # 2': data['Sensor Serial # 2'] } : {'Sensor Serial # 2': '-'}),
                            ...(data.have_another_sensor ? (data['Order Date # 2'] ? { 'Order Date # 2': data['Order Date # 2'] } : {}) : {'Order Date # 2': '-'}),
                            ...(data.have_another_sensor ? (data['Order Number # 2'] ? { 'Order Number # 2': data['Order Number # 2'] } : {}) : {'Order Number # 2': '-'}),
                        }
                    }
                }
            })
        });

        this.#setFormStatus(true, 'success');
    } catch (error) {
        console.error(error);
        this.#setFormStatus(true, 'error', 'Something went wrong. Please try again later.');
    }

    this.#setLoading(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (!this.form) return;

    this.form.removeEventListener('submit', this.#handleFormSubmit.bind(this));
  }
}

if (!customElements.get('sensor-registration-component')) {
  customElements.define('sensor-registration-component', SensorRegistrationComponent);
}
